import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { LuCopy, LuSave } from 'react-icons/lu';
import { Button, Drawer, SegmentedControl } from 'components/common';
import EmployeeRecipientSelect from 'components/common/select/EmployeeRecipientSelect';
import { updateSalesUnitProcessFlow } from 'redux/crm/salesUnitSlice';
import { FLOW_SECTIONS, FLOW_TYPES, getCodes, normalizeFlow, roleKey, setCodes } from './processFlowSchema';

const initialFlows = (salesUnit) => ({
  build_up_flow: normalizeFlow(salesUnit?.build_up_flow),
  hot_roll_flow: normalizeFlow(salesUnit?.hot_roll_flow),
});

const ProcessFlowEditorDrawer = ({ show, onHide, salesUnit, onSaved }) => {
  const dispatch = useDispatch();
  const [flows, setFlows] = useState(() => initialFlows(salesUnit));
  const [activeType, setActiveType] = useState(FLOW_TYPES[0].key);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setFlows(initialFlows(salesUnit));
    setActiveType(FLOW_TYPES[0].key);
  }, [show, salesUnit]);

  const updateRole = (path, codes) => setFlows((current) => ({
    ...current,
    [activeType]: setCodes(current[activeType], path, codes),
  }));

  const copyFromBuildUp = () => setFlows((current) => ({ ...current, hot_roll_flow: normalizeFlow(current.build_up_flow) }));

  const save = async () => {
    setSaving(true);
    try {
      // The reducer stores the returned record and toasts the API message.
      await dispatch(updateSalesUnitProcessFlow({ salesUnitId: salesUnit.id, data: flows })).unwrap();
      onHide();
      onSaved?.();
    } catch (error) {
      toast.error(error?.message || 'Failed to update process flow.');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
      <Button variant="primary" leftIcon={LuSave} onClick={save} loading={saving}>Save process flow</Button>
    </>
  );

  return (
    <Drawer show={show} onHide={onHide} title={`Process flow — ${salesUnit?.name || ''}`} width="52%" minWidth="520px" footer={footer}>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-4">
        <SegmentedControl
          ariaLabel="Process flow type"
          value={activeType}
          onChange={setActiveType}
          options={FLOW_TYPES.map((type) => ({ value: type.key, label: type.label }))}
        />
        {activeType === 'hot_roll_flow' && (
          <Button size="sm" variant="ghost" leftIcon={LuCopy} onClick={copyFromBuildUp}>Copy from Build up</Button>
        )}
      </div>

      {FLOW_SECTIONS.map((section) => (
        <section key={section.key} className="tw-mb-5">
          <h6 className="tw-text-[11px] tw-font-bold tw-text-primary-600 tw-uppercase tw-tracking-wider tw-mb-3 tw-pb-2 tw-border-0 tw-border-b tw-border-solid tw-border-slate-100">
            {section.label}
          </h6>
          <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-2 tw-gap-3">
            {section.roles.map((role) => (
              <label key={roleKey(role.path)} className="tw-flex tw-flex-col tw-gap-1 tw-mb-0">
                <span className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide">{role.label}</span>
                <div className="tw-rounded tw-border tw-border-solid tw-border-slate-200 tw-bg-white">
                  <EmployeeRecipientSelect
                    value={getCodes(flows[activeType], role.path)}
                    onChange={(codes) => updateRole(role.path, codes)}
                    placeholder="Select employees…"
                    isDisabled={saving}
                  />
                </div>
              </label>
            ))}
          </div>
        </section>
      ))}
    </Drawer>
  );
};

ProcessFlowEditorDrawer.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  salesUnit: PropTypes.object,
  onSaved: PropTypes.func,
};

export default ProcessFlowEditorDrawer;
